/************************************************************************************* 
BfxAutoCompleteComponent is based on the @bfx/primeng component developed by PrimeTek, 
used under the following license:

The MIT License (MIT)

Copyright (c) 2016-2018 PrimeTek

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. 
******************************************************************************************/

import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from "@angular/core";
import { DomHandler } from "@bfx/primeng/dom";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";

@Directive({
  selector: "[bfxTooltip]",
  providers: [DomHandler],
  exportAs: "bfxTooltip",
  standalone: false,
})
/* IMPORTANT: this is from @bfx/primeng ver 6.1.3., so far without alteration*/
export class BfxTooltipDirective implements AfterViewInit, OnDestroy {
  @Input() tooltipPosition: string = "right";

  @Input() tooltipEvent: string = "hover";

  /** @deprecated No longer used - tooltip always appended to the referenced element's document body */
  @Input() appendTo: any = "body";

  @Input() positionStyle: string;

  @Input() tooltipStyleClass: string;

  @Input() tooltipZIndex: string = "auto";

  @Input("tooltipDisabled") disabled: boolean = false;

  @Input() escape: boolean = true;

  @Input() showDelay: number;

  @Input() hideDelay: number;

  @Input() life: number;

  container: HTMLDivElement;

  styleClass: string;

  tooltipText: any;

  showTimeout: any;

  hideTimeout: any;

  active: boolean;

  _text: string;

  mouseEnterListener: (e: Event) => void;

  mouseLeaveListener: (e: Event) => void;

  clickListener: (e: Event) => void;

  focusListener: (e: Event) => void;

  blurListener: (e: Event) => void;

  resizeListener: any;

  ac: AbortController;
  acResize: AbortController;

  constructor(
    public el: ElementRef,
    public domHandler: DomHandler,
    public zone: NgZone,
  ) {}

  ngAfterViewInit() {
    this.ac = new AbortController();

    try {
      this.el.nativeElement.style["webkitAppRegion"] = "no-drag";
    } catch (e) {
      // ignore non-webkit
    }

    if (this.tooltipEvent === "hover") {
      this.el.nativeElement.addEventListener("pointerenter", (_e) => this.onMouseEnter(_e), {
        passive: true,
        abort: this.ac.signal,
      });
      this.el.nativeElement.addEventListener("pointerleave", (_e) => this.onMouseLeave(_e), {
        passive: true,
        abort: this.ac.signal,
      });
      this.el.nativeElement.addEventListener("click", (_e) => this.onClick(_e), {
        passive: true,
        abort: this.ac.signal,
      });
    } else if (this.tooltipEvent === "focus") {
      this.el.nativeElement.addEventListener("focus", (_e) => this.onFocus(_e), {
        passive: true,
        abort: this.ac.signal,
      });
      this.el.nativeElement.addEventListener("blur", (_e) => this.onBlur(_e), {
        passive: true,
        abort: this.ac.signal,
      });
    }
  }

  onMouseEnter(_e: Event) {
    if (!this.container && !this.showTimeout) {
      this.activate();
    }
  }

  onMouseLeave(_e: Event) {
    this.deactivate();
  }

  onFocus(_e: Event) {
    this.activate();
  }

  onBlur(_e: Event) {
    this.deactivate();
  }

  onClick(_e: Event) {
    this.deactivate();
  }

  activate() {
    this.active = true;
    this.clearHideTimeout();

    if (this.showDelay)
      this.showTimeout = setTimeout(() => {
        this.show();
      }, this.showDelay);
    else this.show();

    if (this.life) {
      let duration = this.showDelay ? this.life + this.showDelay : this.life;
      this.hideTimeout = setTimeout(() => {
        this.hide();
      }, duration);
    }
  }

  deactivate() {
    this.active = false;
    this.clearShowTimeout();

    if (this.hideDelay) {
      this.clearHideTimeout(); //life timeout
      this.hideTimeout = setTimeout(() => {
        this.hide();
      }, this.hideDelay);
    } else {
      this.hide();
    }
  }

  get text(): string {
    return this._text;
  }

  @Input("bfxTooltip") set text(text: string) {
    this._text = text;
    if (this.active) {
      if (this._text) {
        if (this.container && this.container.offsetParent) this.updateText();
        else this.show();
      } else {
        this.hide();
      }
    }
  }

  create() {
    this.container = document.createElement("div");

    let tooltipArrow = document.createElement("div");
    tooltipArrow.className = "bfx-tooltip-caret";
    this.container.appendChild(tooltipArrow);

    this.tooltipText = document.createElement("div");
    this.tooltipText.className = "bfx-tooltip-text";

    this.updateText();

    if (this.positionStyle) {
      this.container.style.position = this.positionStyle;
    }

    this.container.appendChild(this.tooltipText);

    let bodyElement = DomHandler.getBody(this.el.nativeElement);
    if (bodyElement) {
      bodyElement.appendChild(this.container);
    }

    this.container.style.display = "inline-block";
  }

  show() {
    if (!this.text || this.disabled) {
      return;
    }

    this.create();
    this.align();
    if (NativeDesktopSupport.isNativeDesktopContainer() === false)
      DomHandler.fadeIn(this.container, 250);

    if (this.tooltipZIndex === "auto")
      this.container.style.zIndex = (++DomHandler.zindex).toFixed(0);
    else this.container.style.zIndex = this.tooltipZIndex;

    this.bindDocumentResizeListener();
  }

  hide() {
    this.remove();
  }

  updateText() {
    if (this.escape) {
      this.tooltipText.innerHTML = "";
      this.tooltipText.appendChild(document.createTextNode(this._text));
    } else {
      this.tooltipText.innerHTML = this._text;
    }
  }

  align() {
    let position = this.tooltipPosition;

    switch (position) {
      case "top":
        this.alignTop();
        if (this.isOutOfBounds()) {
          this.alignBottom();
        }
        break;

      case "bottom":
        this.alignBottom();
        if (this.isOutOfBounds()) {
          this.alignTop();
        }
        break;

      case "left":
        this.alignLeft();
        if (this.isOutOfBounds()) {
          this.alignRight();

          if (this.isOutOfBounds()) {
            this.alignTop();

            if (this.isOutOfBounds()) {
              this.alignBottom();
            }
          }
        }
        break;

      case "right":
        this.alignRight();
        if (this.isOutOfBounds()) {
          this.alignLeft();

          if (this.isOutOfBounds()) {
            this.alignTop();

            if (this.isOutOfBounds()) {
              this.alignBottom();
            }
          }
        }
        break;
    }
  }

  getHostOffset() {
    let offset = this.el.nativeElement.getBoundingClientRect();
    let targetLeft = offset.left + DomHandler.getWindowScrollLeft();
    let targetTop = offset.top + DomHandler.getWindowScrollTop();

    return { left: targetLeft, top: targetTop };
  }

  alignRight() {
    this.preAlign("right");
    let hostOffset = this.getHostOffset();
    let left = hostOffset.left + DomHandler.getOuterWidth(this.el.nativeElement);
    let top =
      hostOffset.top +
      (DomHandler.getOuterHeight(this.el.nativeElement) -
        DomHandler.getOuterHeight(this.container)) /
        2;
    this.container.style.left = Math.max(0, left) + "px";
    this.container.style.top = Math.max(0, top) + "px";
  }

  alignLeft() {
    this.preAlign("left");
    let hostOffset = this.getHostOffset();
    let left = hostOffset.left - DomHandler.getOuterWidth(this.container);
    let top =
      hostOffset.top +
      (DomHandler.getOuterHeight(this.el.nativeElement) -
        DomHandler.getOuterHeight(this.container)) /
        2;
    this.container.style.left = Math.max(0, left) + "px";
    this.container.style.top = Math.max(0, top) + "px";
  }

  alignTop() {
    this.preAlign("top");
    let hostOffset = this.getHostOffset();
    let left =
      hostOffset.left +
      (DomHandler.getOuterWidth(this.el.nativeElement) - DomHandler.getOuterWidth(this.container)) /
        2;
    let top = hostOffset.top - DomHandler.getOuterHeight(this.container);
    this.container.style.left = Math.max(0, left) + "px";
    this.container.style.top = Math.max(0, top) + "px";
  }

  alignBottom() {
    this.preAlign("bottom");
    let hostOffset = this.getHostOffset();
    let left =
      hostOffset.left +
      (DomHandler.getOuterWidth(this.el.nativeElement) - DomHandler.getOuterWidth(this.container)) /
        2;
    let top = hostOffset.top + DomHandler.getOuterHeight(this.el.nativeElement);
    this.container.style.left = Math.max(0, left) + "px";
    this.container.style.top = Math.max(0, top) + "px";
  }

  preAlign(position: string) {
    this.container.style.left = -999 + "px";
    this.container.style.top = -999 + "px";

    let defaultClassName = "bfx-tooltip-container bfx-tooltip-" + position;
    this.container.className = this.tooltipStyleClass
      ? defaultClassName + " " + this.tooltipStyleClass
      : defaultClassName;
  }

  isOutOfBounds(): boolean {
    let offset = this.container.getBoundingClientRect();
    let targetTop = offset.top;
    let targetLeft = offset.left;
    let width = DomHandler.getOuterWidth(this.container);
    let height = DomHandler.getOuterHeight(this.container);
    let viewport = DomHandler.getViewport(this.container);

    return (
      targetLeft + width > viewport.width ||
      targetLeft < 0 ||
      targetTop < 0 ||
      targetTop + height > viewport.height
    );
  }

  onWindowResize(_e: Event) {
    this.hide();
  }

  bindDocumentResizeListener() {
    this.unbindDocumentResizeListener();

    this.acResize = new AbortController();

    DomHandler.getWindow(this.el.nativeElement).addEventListener(
      "resize",
      (e) => {
        if (typeof this.resizeListener === "function") {
          this.resizeListener(e);
        }
      },
      {
        passive: true,
        abort: this.acResize.signal,
      } as unknown,
    );
  }

  unbindDocumentResizeListener() {
    if (this.acResize) {
      this.acResize.abort();
      this.acResize = null;
    }
  }

  unbindEvents() {
    if (this.ac != null) {
      this.ac.abort();
      this.ac = null;
    }

    this.unbindDocumentResizeListener();
  }

  remove() {
    if (this.container && this.container.parentElement) {
      let bodyElement = DomHandler.getBody(this.el.nativeElement);
      if (bodyElement) {
        bodyElement.removeChild(this.container);
      }
    }

    this.unbindDocumentResizeListener();
    this.clearTimeouts();
    this.container = null;
  }

  clearShowTimeout() {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
  }

  clearHideTimeout() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  clearTimeouts() {
    this.clearShowTimeout();
    this.clearHideTimeout();
  }

  ngOnDestroy() {
    this.unbindEvents();
    this.remove();
  }
}
